import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface WorkflowStage {
  id?: string;
  stage_name: string;
  stage_key: string;
  stage_order: number;
  color: string;
  job_status?: string;
  lead_status?: string;
}

export interface Workflow {
  id?: string;
  org_id: string;
  workflow_type: 'retail' | 'insurance' | 'commercial';
  name: string;
  description?: string;
  is_active: boolean;
  is_locked?: boolean; // System workflows that cannot be edited
  stages: WorkflowStage[];
}

// Industry-standard default workflows - fully customizable
const DEFAULT_WORKFLOWS = {
  retail: {
    name: 'Retail/Standard Workflow',
    description: 'Standard residential roofing workflow for direct-to-consumer sales',
    isLocked: false,
    stages: [
      { stage_name: 'Lead', stage_key: 'lead', stage_order: 1, color: '#3b82f6', job_status: 'new' },
      { stage_name: 'Contacted', stage_key: 'contacted', stage_order: 2, color: '#a855f7', job_status: 'contacted' },
      { stage_name: 'Appointment Set', stage_key: 'appointment_set', stage_order: 3, color: '#f97316', job_status: 'scheduled' },
      { stage_name: 'Proposal Sent', stage_key: 'proposal_sent', stage_order: 4, color: '#06b6d4', job_status: 'inspected' },
      { stage_name: 'Closed Won', stage_key: 'closed_won', stage_order: 5, color: '#10b981', job_status: 'completed' },
      { stage_name: 'Closed Lost', stage_key: 'closed_lost', stage_order: 6, color: '#ef4444', job_status: 'lost' },
    ]
  },
  commercial: {
    name: 'Commercial Workflow',
    description: 'Commercial roofing workflow with bidding and tender processes',
    isLocked: false,
    stages: [
      { stage_name: 'Lead', stage_key: 'lead', stage_order: 1, color: '#3b82f6', job_status: 'new' },
      { stage_name: 'Site Visit/Inspection', stage_key: 'site_visit', stage_order: 2, color: '#a855f7', job_status: 'contacted' },
      { stage_name: 'Bidding/Tender', stage_key: 'bidding', stage_order: 3, color: '#f59e0b', job_status: 'scheduled' },
      { stage_name: 'Proposal Submitted', stage_key: 'proposal_submitted', stage_order: 4, color: '#06b6d4', job_status: 'inspected' },
      { stage_name: 'Contract Signed', stage_key: 'contract_signed', stage_order: 5, color: '#8b5cf6', job_status: 'approved' },
      { stage_name: 'In Progress', stage_key: 'in_progress', stage_order: 6, color: '#22c55e', job_status: 'in_progress' },
      { stage_name: 'Closed Won', stage_key: 'closed_won', stage_order: 7, color: '#10b981', job_status: 'completed' },
      { stage_name: 'Closed Lost', stage_key: 'closed_lost', stage_order: 8, color: '#ef4444', job_status: 'lost' },
    ]
  },
  insurance: {
    name: 'Insurance Workflow',
    description: 'Insurance claim workflow with adjuster meetings and claim approval',
    isLocked: false,
    stages: [
      { stage_name: 'Lead', stage_key: 'lead', stage_order: 1, color: '#3b82f6', job_status: 'new' },
      { stage_name: 'Inspection/Claim Filed', stage_key: 'inspection', stage_order: 2, color: '#a855f7', job_status: 'contacted' },
      { stage_name: 'Adjuster Meeting', stage_key: 'adjuster_meeting', stage_order: 3, color: '#f97316', job_status: 'scheduled' },
      { stage_name: 'Claim Approved', stage_key: 'claim_approved', stage_order: 4, color: '#8b5cf6', job_status: 'approved' },
      { stage_name: 'Proposal Sent', stage_key: 'proposal_sent', stage_order: 5, color: '#06b6d4', job_status: 'inspected' },
      { stage_name: 'Contract Signed', stage_key: 'contract_signed', stage_order: 6, color: '#14b8a6', job_status: 'materials_ordered' },
      { stage_name: 'In Progress', stage_key: 'in_progress', stage_order: 7, color: '#22c55e', job_status: 'in_progress' },
      { stage_name: 'Closed Won', stage_key: 'closed_won', stage_order: 8, color: '#10b981', job_status: 'completed' },
      { stage_name: 'Closed Lost', stage_key: 'closed_lost', stage_order: 9, color: '#ef4444', job_status: 'lost' },
    ]
  }
};

export const useWorkflows = (orgId: string, workflowType: 'retail' | 'insurance' | 'commercial') => {
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orgId && workflowType) {
      loadWorkflow();
    }
  }, [orgId, workflowType]);

  const loadWorkflow = async () => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Try to load existing workflow
      const { data: workflowData, error: workflowError } = await supabase
        .from('workflows')
        .select(`
          *,
          workflow_stages (*)
        `)
        .eq('org_id', orgId)
        .eq('workflow_type', workflowType)
        .maybeSingle();

      if (workflowError && workflowError.code !== 'PGRST116') {
        throw workflowError;
      }

      if (workflowData) {
        setWorkflow({
          id: workflowData.id,
          org_id: workflowData.org_id,
          workflow_type: workflowData.workflow_type as 'retail' | 'insurance' | 'commercial',
          name: workflowData.name,
          description: workflowData.description || undefined,
          is_active: workflowData.is_active,
          is_locked: workflowData.is_locked || false,
          stages: (workflowData.workflow_stages || []).sort((a: WorkflowStage, b: WorkflowStage) => 
            a.stage_order - b.stage_order
          )
        });
      } else {
        // Use locked default workflow if none exists
        const defaultWorkflow = DEFAULT_WORKFLOWS[workflowType];
        setWorkflow({
          org_id: orgId,
          workflow_type: workflowType,
          name: defaultWorkflow.name,
          description: defaultWorkflow.description,
          is_active: true,
          is_locked: defaultWorkflow.isLocked,
          stages: defaultWorkflow.stages
        });
      }
    } catch (error) {
      console.error('Error loading workflow:', error);
      toast.error('Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  const saveWorkflow = async (updatedWorkflow: Workflow) => {
    try {
      // Upsert workflow
      const { data: workflowData, error: workflowError } = await supabase
        .from('workflows')
        .upsert({
          id: updatedWorkflow.id,
          org_id: orgId,
          workflow_type: workflowType,
          name: updatedWorkflow.name,
          description: updatedWorkflow.description,
          is_active: updatedWorkflow.is_active,
          is_locked: false, // User-created workflows are never locked
        })
        .select()
        .single();

      if (workflowError) throw workflowError;

      // Delete existing stages
      if (workflowData.id) {
        await supabase
          .from('workflow_stages')
          .delete()
          .eq('workflow_id', workflowData.id);
      }

      // Insert new stages
      const stagesToInsert = updatedWorkflow.stages.map((stage, index) => ({
        workflow_id: workflowData.id,
        stage_name: stage.stage_name,
        stage_key: stage.stage_key,
        stage_order: index + 1,
        color: stage.color,
        job_status: stage.job_status,
        lead_status: stage.lead_status,
      }));

      const { error: stagesError } = await supabase
        .from('workflow_stages')
        .insert(stagesToInsert);

      if (stagesError) throw stagesError;

      toast.success('Workflow saved successfully');
      await loadWorkflow();
      return true;
    } catch (error) {
      console.error('Error saving workflow:', error);
      toast.error('Failed to save workflow');
      return false;
    }
  };

  const resetToDefault = async () => {
    try {
      if (workflow?.id) {
        await supabase
          .from('workflows')
          .delete()
          .eq('id', workflow.id);
      }
      
      toast.success('Workflow reset to default');
      await loadWorkflow();
      return true;
    } catch (error) {
      console.error('Error resetting workflow:', error);
      toast.error('Failed to reset workflow');
      return false;
    }
  };

  return {
    workflow,
    loading,
    saveWorkflow,
    resetToDefault,
    reloadWorkflow: loadWorkflow,
  };
};
